import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Printer, Loader2, AlertTriangle } from 'lucide-react';
import { shiftApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { isCompanyAdmin, isManager, type POSShift, type ShiftCloseReport } from '@/types';
import { browserPrintShiftReceipt } from '@/utils/shiftReceiptPrint';
import { formatMoney } from '@/utils/currency';
import { useShiftStore } from '@/store/shiftStore';

export default function XReportSection() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.role);
  const canPickShift = isCompanyAdmin(role) || isManager(role);
  const openShift = useShiftStore((s) => s.openShift);
  const [selectedShift, setSelectedShift] = useState<string>('');

  const { data: openShifts = [] } = useQuery({
    queryKey: ['listOpenShifts'],
    queryFn: async () => {
      const res = await shiftApi.listOpenShifts();
      return res.data.message as POSShift[];
    },
    enabled: canPickShift,
  });

  const shiftName = useMemo(() => {
    if (canPickShift) return selectedShift || openShifts[0]?.name || '';
    return openShift?.name || '';
  }, [canPickShift, selectedShift, openShifts, openShift]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['xReport', shiftName],
    queryFn: async () => {
      const res = await shiftApi.getXReport({
        shift: shiftName || undefined,
        detail_type: 'invoice',
      });
      return res.data.message as ShiftCloseReport;
    },
    enabled: !!shiftName,
    retry: false,
  });

  const handlePrint = () => {
    if (data) browserPrintShiftReceipt({ ...data, report_type: 'X' });
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-100 text-amber-900 text-sm rounded-xl px-4 py-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <p>{t('xReport.warning')}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        {canPickShift ? (
          <label className="text-sm text-gray-700">
            {t('xReport.openShift')}
            <select
              className="ml-2 border border-gray-200 rounded-lg text-sm px-2 py-1.5"
              value={shiftName}
              onChange={(e) => setSelectedShift(e.target.value)}
            >
              <option value="">{t('xReport.select')}</option>
              {openShifts.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.cashier_name || s.cashier} — {s.pocket_name} ({s.name})
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="text-sm text-gray-600">
            {t('xReport.currentShift')} <strong>{openShift?.name || t('xReport.noneOpen')}</strong>
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching || !shiftName}
            className="text-sm px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            {t('xReport.refresh')}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={!data}
            className="text-sm px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> {t('xReport.printX')}
          </button>
        </div>
      </div>

      {(isLoading || isFetching) && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('xReport.loading')}
        </div>
      )}

      {isError && (
        <div className="text-sm text-red-600">
          {t('xReport.failed')}: {(error as Error)?.message || ''}
        </div>
      )}

      {data && !isLoading && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="font-semibold text-gray-900 mb-4">{t('xReport.title', { name: data.name || shiftName })}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div><p className="text-gray-500">{t('xReport.cashier')}</p><p className="font-medium">{data.cashier_name || data.cashier}</p></div>
            <div><p className="text-gray-500">{t('xReport.drawer')}</p><p className="font-medium">{data.pocket_name}</p></div>
            <div><p className="text-gray-500">{t('xReport.invoices')}</p><p className="font-medium">{data.invoice_count ?? 0}</p></div>
            <div><p className="text-gray-500">{t('xReport.expectedCash')}</p><p className="font-medium">{formatMoney(data.expected_cash ?? 0)}</p></div>
            <div><p className="text-gray-500">{t('xReport.totalSales')}</p><p className="font-medium">{formatMoney(data.total_sales ?? 0)}</p></div>
            <div><p className="text-gray-500">{t('xReport.cashSales')}</p><p className="font-medium">{formatMoney(data.cash_sales ?? 0)}</p></div>
            <div><p className="text-gray-500">{t('xReport.cashReturns')}</p><p className="font-medium">{formatMoney(data.cash_returns ?? 0)}</p></div>
            <div><p className="text-gray-500">{t('xReport.drawerExpenses')}</p><p className="font-medium">{formatMoney(data.total_drawer_expenses ?? 0)}</p></div>
            <div><p className="text-gray-500">{t('xReport.nonCash')}</p><p className="font-medium">{formatMoney(Object.values(data.non_cash_breakdown || {}).reduce((a, b) => a + b, 0))}</p></div>
          </div>
        </div>
      )}
    </div>
  );
}
